/* One open file: markdown rendered (or raw), code with line numbers, an image as an
   image, anything else as a download. The bytes are a stranger's, so nothing here is
   ever inserted as HTML — an SVG goes into an <img>, where its scripts cannot run. */
import React, { useEffect, useMemo, useState } from "react";
import { Code, Download, ExternalLink } from "lucide-react";

import { useSkillFile } from "@/shared/api/skills";

import { fileKind, formatBytes } from "../catalog";
import { LoadingDots } from "./atoms";
import SkillMarkdown from "./SkillMarkdown";

import type { ApiError } from "@/shared/api/errors";
import type { SkillDetail, SkillFile } from "@/shared/types";

// Past this, the DOM gets slow long before anyone reads that far.
const MAX_LINES = 3000;

function CodeView({ text }: { text: string }) {
  const all = text.replace(/\n$/, "").split("\n");
  const lines = all.slice(0, MAX_LINES);
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse font-mono text-[11.5px] leading-[1.6]">
        <tbody>
          {lines.map((line, i) => (
            <tr key={i}>
              <td className="select-none pl-3 pr-3 text-right align-top tabular-nums text-[var(--text-quaternary)]">{i + 1}</td>
              <td className="pr-4 whitespace-pre text-[var(--text-secondary)]">{line || " "}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {all.length > MAX_LINES && (
        <div className="px-3 py-2 text-[12px] text-[var(--text-tertiary)]">
          Showing the first {MAX_LINES.toLocaleString()} of {all.length.toLocaleString()} lines — download the file for the rest.
        </div>
      )}
    </div>
  );
}

/** A URL for the bytes, alive while the component is. An SVG arrives as text, so it is
    re-typed to be drawn as an image. */
function useObjectUrl(blob: Blob | undefined, path: string): string | null {
  const url = useMemo(() => {
    if (!blob) return null;
    const typed = /\.svg$/i.test(path) ? new Blob([blob], { type: "image/svg+xml" }) : blob;
    return URL.createObjectURL(typed);
  }, [blob, path]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return url;
}

function download(blob: Blob, path: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = path.split("/").pop() || "file";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function githubUrl(skill: SkillDetail, path: string): string | null {
  if (!skill.repo_url || !skill.commit) return null;
  const folder = skill.folder ? `${skill.folder}/` : "";
  return `${skill.repo_url}/blob/${skill.commit}/${folder}${path}`;
}

export default function FileViewer({ skill, file }: { skill: SkillDetail; file: SkillFile }) {
  const q = useSkillFile(skill.id, file.path);
  const data = q.data && { ...q.data, kind: fileKind(file.path, q.data.text !== null) };
  const [raw, setRaw] = useState(false);
  const url = useObjectUrl(data?.kind === "image" ? data.blob : undefined, file.path);
  const onGitHub = githubUrl(skill, file.path);
  const iconButton =
    "press w-7 h-7 shrink-0 rounded-full hover:bg-[var(--accent-hover)] flex items-center justify-center text-[var(--text-secondary)]";

  return (
    <div className="flex flex-col min-w-0 md:max-h-[560px]">
      <div className="h-11 shrink-0 flex items-center gap-2 px-3 border-b hairline">
        <code className="flex-1 min-w-0 truncate text-[12.5px] text-[var(--text-primary)]" title={file.path}>{file.path}</code>
        <span className="shrink-0 text-[11.5px] font-mono tabular-nums text-[var(--text-quaternary)]">{formatBytes(file.size)}</span>
        {data?.kind === "markdown" && (
          <button
            onClick={() => setRaw((v) => !v)}
            aria-pressed={raw}
            className={`press shrink-0 flex items-center gap-1.5 h-7 px-2.5 rounded-[8px] text-[12px] ring-1 ring-[var(--hairline)] ${
              raw
                ? "bg-[var(--accent-active)] text-[var(--text-primary)] font-medium"
                : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
            }`}
          >
            <Code className="w-3 h-3" strokeWidth={1.75} />
            {raw ? "Rendered" : "Raw"}
          </button>
        )}
        {data && (
          <button onClick={() => download(data.blob, file.path)} title="Download" aria-label="Download" className={iconButton}>
            <Download className="w-3.5 h-3.5" strokeWidth={1.75} />
          </button>
        )}
        {onGitHub && (
          <a href={onGitHub} target="_blank" rel="noreferrer" title="Open on GitHub" aria-label="Open on GitHub" className={iconButton}>
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.75} />
          </a>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {q.isPending && <LoadingDots label="Loading file" />}
        {q.isError && (
          <div className="py-12 px-4 flex flex-col items-center gap-3 text-center">
            <div className="text-[13px] text-[var(--text-secondary)]">
              {(q.error as ApiError).status === 404
                ? "This file's contents aren't stored — the skill needs importing again."
                : "Couldn't load this file."}
            </div>
            {(q.error as ApiError).status !== 404 && (
              <button
                onClick={() => q.refetch()}
                className="press h-8 px-3.5 rounded-[8px] ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] text-[13px] text-[var(--text-primary)] hover:bg-[var(--surface-sunk)] hover:ring-[var(--hairline-focus)]"
              >
                Try again
              </button>
            )}
          </div>
        )}
        {data?.kind === "markdown" && (raw ? <CodeView text={data.text ?? ""} /> : (
          <div className="px-5 py-4">
            <SkillMarkdown text={data.text ?? ""} />
          </div>
        ))}
        {data?.kind === "text" && <CodeView text={data.text ?? ""} />}
        {data?.kind === "image" && url && (
          <div className="p-4 flex justify-center bg-[var(--surface-sunk)]">
            <img src={url} alt={file.path} className="max-w-full object-contain" />
          </div>
        )}
        {data?.kind === "binary" && (
          <div className="py-12 px-4 flex flex-col items-center gap-3 text-center">
            <div className="text-[13px] text-[var(--text-secondary)]">Binary file — it can't be shown here.</div>
            <button
              onClick={() => download(data.blob, file.path)}
              className="press h-8 px-3.5 rounded-[8px] ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] text-[13px] text-[var(--text-primary)] hover:bg-[var(--surface-sunk)] hover:ring-[var(--hairline-focus)] flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.75} />
              Download
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
